import { Warning } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";

export const Pill = ({ children, tone = "neutral", className, ...rest }) => {
  const tones = {
    neutral: "bg-slate-100 text-slate-700 border-slate-200",
    long: "bg-[#16a34a]/10 text-[#16a34a] border-[#16a34a]/30",
    short: "bg-[#dc2626]/10 text-[#dc2626] border-[#dc2626]/30",
    cyan: "bg-[#2563eb]/10 text-[#2563eb] border-[#2563eb]/30",
    yellow: "bg-[#d97706]/10 text-[#d97706] border-[#d97706]/30",
    rejected: "bg-slate-100 text-slate-600 border-slate-300",
    success: "bg-[#16a34a]/10 text-[#16a34a] border-[#16a34a]/30",
    error: "bg-[#dc2626]/10 text-[#dc2626] border-[#dc2626]/30",
    info: "bg-[#2563eb]/10 text-[#2563eb] border-[#2563eb]/30",
    warn: "bg-[#d97706]/10 text-[#d97706] border-[#d97706]/30",
  };
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-bold tracking-[0.1em] uppercase border font-data",
        tones[tone] || tones.neutral,
        className,
      )}
      {...rest}
    >
      {children}
    </span>
  );
};

export const GradePill = ({ grade }) => {
  const tone =
    grade === "A+" ? "cyan" : grade === "A" ? "success" : grade === "B" ? "yellow" : "neutral";
  return <Pill tone={tone}>{grade || "—"}</Pill>;
};

export const DirectionPill = ({ direction }) => (
  <Pill tone={direction === "LONG" ? "long" : "short"}>{direction}</Pill>
);

const STATUS_META = {
  ACTIVE: { tone: "info", label: "Active" },
  HIT_TP1: { tone: "success", label: "TP1 Hit" },
  HIT_TP2: { tone: "success", label: "TP2 Hit" },
  HIT_TP: { tone: "success", label: "All TP Hit" },
  HIT_SL: { tone: "error", label: "Stopped Out" },
  HIT_SL_PARTIAL_TP: { tone: "warn", label: "SL · Partial TP" },
  EXPIRED: { tone: "neutral", label: "Expired" },
};

export const StatusPill = ({ status, ...rest }) => {
  const meta = STATUS_META[status] || { tone: "neutral", label: status || "—" };
  return (
    <Pill tone={meta.tone} {...rest}>
      {meta.label}
    </Pill>
  );
};

// Advisory-only thesis-invalidation indicator (see thesis_invalidation.py —
// never auto-closes a position, just flags it). Icon-only + tooltip so it
// stays compact in dense table cells; confirmed (thesis_invalidated) renders
// filled/solid amber, a pending/unconfirmed sighting on just one scan cycle
// (thesis_warning_reason) renders a lighter outline so it reads as "watching"
// rather than "acted on". Pages with more room (e.g. SignalDetail) build
// their own expanded banner using the same underlying fields.
export const ThesisInvalidationBadge = ({ signal, ...rest }) => {
  if (!signal) return null;
  if (signal.thesis_invalidated) {
    return (
      <span
        title={`Thesis invalidated: ${signal.thesis_invalidated_reason || "entry thesis no longer holds"}`}
        className="inline-flex items-center text-[#d97706]"
        {...rest}
      >
        <Warning size={13} weight="fill" />
      </span>
    );
  }
  if (signal.thesis_warning_reason) {
    return (
      <span
        title={`Watching: ${signal.thesis_warning_reason}`}
        className="inline-flex items-center text-[#d97706]/60"
        {...rest}
      >
        <Warning size={13} weight="regular" />
      </span>
    );
  }
  return null;
};
